import { Heart, FileDown, GitCompareArrows, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import CartPicker from '../common/CartPicker';
import type { Product } from '../../types/product';
import type { SortOption } from '../../pages/ProductListPage';
import { useAuth } from '../../hooks/useAuth';
import { useFavoriteActions } from '../../hooks/useFavoriteActions';
import { useCartActions } from '../../hooks/useCartActions';
import { useCompareActions } from '../../hooks/useCompareActions';
import { useSettingsStore } from '../../stores/useSettingsStore';
import AnimatedActionButton from '../common/AnimatedActionButton';
import HighlightedText from '../common/HighlightedText';
import FavoriteGroupPicker from '../common/FavoriteGroupPicker';
import MarketingClaimBadge from './MarketingClaimBadge';
import NewBadge from './NewBadge';
import NewStudyBadge from './NewStudyBadge';
import TerritoryAgreementPill from './TerritoryAgreementPill';
import { formatDose } from '../../utils/formatDose';
import { useTranslation } from '../../i18n';

interface ProductTableProps {
  products: Product[];
  onProductClick: (id: string) => void;
  sortOption?: SortOption | null;
  onSortChange?: (sort: SortOption | null) => void;
  searchTerm?: string;
}

/**
 * Mapping from column key to its ascending / descending SortOption values.
 * Column headers cycle: none → asc → desc → none.
 */
const COLUMN_SORT_MAP: Record<string, { asc: SortOption; desc: SortOption }> = {
  articleNumber: { asc: 'articleNumber-asc', desc: 'articleNumber-desc' },
  name: { asc: 'name-asc', desc: 'name-desc' },
  partner: { asc: 'partner-asc', desc: 'partner-desc' },
  indication: { asc: 'indication-asc', desc: 'indication-desc' },
};

/** Render sort indicator icon for a column */
const SortIcon = ({ dir }: { dir: 'asc' | 'desc' | null }) => {
  if (dir === 'asc') return <ArrowUp className="w-3.5 h-3.5 text-vivatis-600 dark:text-vivatis-400" />;
  if (dir === 'desc') return <ArrowDown className="w-3.5 h-3.5 text-vivatis-600 dark:text-vivatis-400" />;
  return <ArrowUpDown className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" />;
};

/** Renders a sortable <th> */
const SortableTh = ({
  dir,
  sortable,
  onSort,
  children,
  className = '',
}: {
  dir: 'asc' | 'desc' | null;
  sortable: boolean;
  onSort: () => void;
  children: React.ReactNode;
  className?: string;
}) => {
  const active = dir !== null;
  return (
    <th
      className={`px-2.5 py-2 xl:px-4 xl:py-3 font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap select-none ${
        sortable ? 'cursor-pointer hover:text-gray-900 dark:hover:text-gray-100 transition-colors' : ''
      } ${className}`}
      onClick={onSort}
    >
      <span className="inline-flex items-center gap-1.5">
        <span className={active ? 'text-gray-900 dark:text-gray-100' : ''}>{children}</span>
        {sortable && <SortIcon dir={dir} />}
      </span>
    </th>
  );
};

export default function ProductTable({
  products,
  onProductClick,
  sortOption,
  onSortChange,
  searchTerm = '',
}: ProductTableProps) {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { toggleFavorite, isFavorite } = useFavoriteActions(isLoggedIn);
  const { addToCart, removeFromCart, isInCart } = useCartActions(isLoggedIn);
  const { toggleCompare, isInCompare } = useCompareActions(isLoggedIn);
  const { favoriteGroupsEnabled, multiCartEnabled } = useSettingsStore();

  /** Returns which sort direction is active for a column key, or null */
  const getColumnSortDir = (
    key: string,
  ): 'asc' | 'desc' | null => {
    const map = COLUMN_SORT_MAP[key];
    if (!map || !sortOption) return null;
    if (sortOption === map.asc) return 'asc';
    if (sortOption === map.desc) return 'desc';
    return null;
  };

  /** Handle clicking a sortable header — cycles none → asc → desc → none. */
  const handleSort = (key: string) => {
    if (!onSortChange) return;
    const map = COLUMN_SORT_MAP[key];
    if (!map) return;

    const current = getColumnSortDir(key);
    if (current === null) {
      onSortChange(map.asc);
    } else if (current === 'asc') {
      onSortChange(map.desc);
    } else {
      onSortChange(null);
    }
  };

  return (
    <div className="overflow-x-auto rounded-xl overflow-hidden bg-white dark:bg-gray-800">
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800">
            <SortableTh dir={getColumnSortDir('articleNumber')} sortable={!!onSortChange} onSort={() => handleSort('articleNumber')}>
              Article No.
            </SortableTh>
            <SortableTh dir={getColumnSortDir('name')} sortable={!!onSortChange} onSort={() => handleSort('name')}>
              Name
            </SortableTh>
            <SortableTh dir={getColumnSortDir('partner')} sortable={!!onSortChange} onSort={() => handleSort('partner')}>
              Partner
            </SortableTh>
            <SortableTh dir={getColumnSortDir('indication')} sortable={!!onSortChange} onSort={() => handleSort('indication')}>
              Indication
            </SortableTh>
            <th className="px-2.5 py-2 xl:px-4 xl:py-3 font-medium text-gray-600 dark:text-gray-300 hidden xl:table-cell">
              Application
            </th>
            <th className="px-2.5 py-2 xl:px-4 xl:py-3 font-medium text-gray-600 dark:text-gray-300 hidden lg:table-cell">
              Country
            </th>
            <th className="px-2.5 py-2 xl:px-4 xl:py-3 font-medium text-gray-600 dark:text-gray-300 hidden xl:table-cell whitespace-nowrap">
              Daily Dose
            </th>
            <th className="px-2.5 py-2 xl:px-4 xl:py-3 font-medium text-gray-600 dark:text-gray-300">
              Claims
            </th>
            <th className="px-2.5 py-2 xl:px-4 xl:py-3 font-medium text-gray-600 dark:text-gray-300 text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {products.map((product, index) => {
            const productId = String(product.id);
            const favorited = isFavorite(productId);
            const inCart = isInCart(product.id);
            const inCompare = isInCompare(productId);
            const rowBg = index % 2 === 0
              ? 'bg-white dark:bg-gray-800'
              : 'bg-gray-50/50 dark:bg-gray-800/50';

            return (
              <tr
                key={product.id}
                onClick={() => onProductClick(productId)}
                className={`cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors ${rowBg}`}
              >
                {/* Article Number */}
                <td className="px-2.5 py-2 xl:px-4 xl:py-3 whitespace-nowrap text-gray-500 dark:text-gray-400 font-mono text-xs">
                  <HighlightedText text={product.articleNumber} term={searchTerm} />
                </td>

                {/* Name + NEW badge */}
                <td className="px-2.5 py-2 xl:px-4 xl:py-3 min-w-56">
                  <div className="flex items-start gap-2 min-w-0">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      <HighlightedText text={product.name} term={searchTerm} />
                    </span>
                    {product.isNew && <NewBadge />}
                    {product.hasNewStudy && <NewStudyBadge />}
                  </div>
                </td>

                {/* Partner */}
                <td className="px-2.5 py-2 xl:px-4 xl:py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  <HighlightedText text={product.partner?.name} term={searchTerm} />
                </td>

                {/* Indication */}
                <td className="px-2.5 py-2 xl:px-4 xl:py-3">
                  <div className="flex flex-wrap gap-1">
                    {product.indications.map((ind) => (
                      <span
                        key={ind.id}
                        className="inline-block rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-[11px] text-gray-600 dark:text-gray-300 whitespace-nowrap"
                      >
                        <HighlightedText text={ind.name} term={searchTerm} />
                      </span>
                    ))}
                  </div>
                </td>

                {/* Application (hidden on small) */}
                <td className="px-2.5 py-2 xl:px-4 xl:py-3 text-gray-600 dark:text-gray-300 hidden xl:table-cell">
                  <div className="flex flex-col gap-0.5">
                    {product.applications.map((app) => (
                      <span
                        key={app.id}
                        className="text-xs whitespace-nowrap"
                      >
                        <HighlightedText text={app.name} term={searchTerm} />
                      </span>
                    ))}
                  </div>
                </td>

                {/* Country (hidden on small) */}
                <td className="px-2.5 py-2 xl:px-4 xl:py-3 text-gray-600 dark:text-gray-300 hidden lg:table-cell">
                  <div className="flex flex-wrap gap-1 text-xs">
                    {(() => {
                      return (
                        <>
                          {product.territories.slice(0, 3).map((t) => (
                            <TerritoryAgreementPill key={t.id} territory={t} />
                          ))}
                          {product.territories.length > 3 && (
                            <span className="text-gray-400 dark:text-gray-500">
                              +{product.territories.length - 3}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </td>

                {/* Daily Dose (hidden on small) */}
                <td className="px-2.5 py-2 xl:px-4 xl:py-3 text-gray-600 dark:text-gray-300 hidden xl:table-cell whitespace-nowrap text-xs">
                  {formatDose(product)}
                </td>

                {/* Marketing Claims */}
                <td className="px-2.5 py-2 xl:px-4 xl:py-3">
                  <div className="flex flex-wrap gap-1">
                    {product.marketingClaims.map((claim) => (
                      <MarketingClaimBadge key={claim.id} claim={claim} />
                    ))}
                  </div>
                </td>

                {/* Actions */}
                <td className="px-2.5 py-2 xl:px-4 xl:py-3">
                  <div className="flex items-center justify-end gap-1">
                    {favoriteGroupsEnabled ? (
                      <FavoriteGroupPicker
                        productId={productId}
                        onSimpleToggle={(e) => { e.stopPropagation(); toggleFavorite(productId); }}
                        align="right"
                        direction="down"
                      />
                    ) : (
                      <AnimatedActionButton
                        title={favorited ? t.actions.removeFromFavorites : t.actions.addToFavorites}
                        className="dark:hover:bg-gray-600"
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(productId); }}
                        icon={<Heart className={`w-4 h-4 transition-colors ${favorited ? 'fill-red-500 text-red-500' : 'text-gray-400 dark:text-gray-500 group-hover:text-red-500 dark:group-hover:text-red-400'}`} />}
                      />
                    )}
                    {multiCartEnabled ? (
                      <>
                        <span className="md:hidden">
                          <CartPicker productId={product.id} direction="up" />
                        </span>
                        <span className="hidden md:inline">
                          <AnimatedActionButton
                            title={inCart ? t.actions.removeFromCart : t.actions.addToCart}
                            className="dark:hover:bg-gray-600"
                            onClick={(e) => { e.stopPropagation(); if (inCart) removeFromCart(product.id); else addToCart(product.id); }}
                            icon={<FileDown className={`w-4 h-4 transition-colors ${inCart ? 'text-vivatis-600 dark:text-vivatis-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-vivatis-600 dark:group-hover:text-vivatis-400'}`} />}
                          />
                        </span>
                      </>
                    ) : (
                      <AnimatedActionButton
                        title={inCart ? t.actions.removeFromCart : t.actions.addToCart}
                        className="dark:hover:bg-gray-600"
                        onClick={(e) => { e.stopPropagation(); if (inCart) removeFromCart(product.id); else addToCart(product.id); }}
                        icon={<FileDown className={`w-4 h-4 transition-colors ${inCart ? 'text-vivatis-600 dark:text-vivatis-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-vivatis-600 dark:group-hover:text-vivatis-400'}`} />}
                      />
                    )}
                    <AnimatedActionButton
                      title={inCompare ? t.actions.removeFromComparison : t.actions.addToComparison}
                      className="dark:hover:bg-gray-600"
                      onClick={(e) => { e.stopPropagation(); toggleCompare(productId); }}
                      icon={<GitCompareArrows className={`w-4 h-4 transition-colors ${inCompare ? 'text-vivatis-600 dark:text-vivatis-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-vivatis-600 dark:group-hover:text-vivatis-400'}`} />}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {products.length === 0 && (
        <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
          No products found.
        </div>
      )}
    </div>
  );
}
